import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dateOnly, getTodayEc } from './clienteMenuApi.js';

function XIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
    );
}

function WarningIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 3l10 18H2L12 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M12 9v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 17h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}

function DishRow({ title, dish }) {
    const [descOpen, setDescOpen] = useState(false);
    const descRef = useRef(null);
    const [canExpandDesc, setCanExpandDesc] = useState(false);

    useEffect(() => {
        setDescOpen(false);
        setCanExpandDesc(false);
    }, [dish?.id]);

    useEffect(() => {
        if (descOpen) return;
        const el = descRef.current;
        if (!el) return;
        setCanExpandDesc(el.scrollHeight > el.clientHeight + 2);
    }, [dish?.id, dish?.descripcion, descOpen]);

    if (!dish) return null;

    const img = Array.isArray(dish.imagenes) && dish.imagenes[0] ? dish.imagenes[0] : '';

    return (
        <div className="tw-orderweek-dishrow">
            <div className="tw-orderweek-dishrow__img">
                {img ? <img src={img} alt={dish.nombre || ''} /> : <div className="tw-orderweek-dishrow__imgph" />}
            </div>

            <div className="tw-orderweek-dishrow__info">
                <div className="tw-orderweek-dishrow__top">
                    <div className="tw-orderweek-dishrow__title">{title}</div>
                    <div className="tw-orderweek-dishrow__name">{dish.nombre || '—'}</div>
                </div>

                {dish?.descripcion ? (
                    <>
                        <div
                            ref={descRef}
                            className={`tw-orderweek-dishrow__desc ${descOpen ? 'is-expanded' : ''}`}
                        >
                            {dish.descripcion}
                        </div>

                        {canExpandDesc ? (
                            <button
                                type="button"
                                className="tw-orderweek-dishrow__more"
                                onClick={() => setDescOpen((v) => !v)}
                                aria-expanded={descOpen ? 'true' : 'false'}
                            >
                                {descOpen ? 'Ver menos' : 'Ver descripción completa'}
                            </button>
                        ) : null}
                    </>
                ) : null}
            </div>
        </div>
    );
}

function safeJsonParse(maybeJson) {
    try {
        if (!maybeJson) return null;
        if (typeof maybeJson === 'object') return maybeJson;
        return JSON.parse(maybeJson);
    } catch {
        return null;
    }
}

export default function ClientePedidoModal({
    open,
    mode = 'view',
    title = '',
    order = null,
    dishMap = {},
    enviosExpiraAt = '',
    onClose,
    onConfirmCancel,
    isLoading = false,
}) {
    const hoy = getTodayEc();
    const expDay = String(enviosExpiraAt || '').slice(0, 10);

    const parsed = useMemo(() => safeJsonParse(order?.dish_info), [order?.dish_info]);

    const selected = parsed?.selected || parsed || {};
    const mainId = selected?.main_id ?? selected?.mainId ?? null;
    const entryId = selected?.entry_id ?? selected?.entryId ?? null;

    const isDinner = String(order?.type || '').trim().toLowerCase() === 'cena';

    const snackIdRaw = selected?.snack_id ?? selected?.snackId ?? null;
    const snackId = isDinner ? null : snackIdRaw;

    const snap = parsed?.snapshot || {};

    const main = snap?.main || (mainId ? (dishMap?.[String(mainId)] || null) : null);
    const entry = entryId ? (snap?.entry || dishMap?.[String(entryId)] || null) : null;
    const snack = snackId ? (snap?.snack || dishMap?.[String(snackId)] || null) : null;

    const orderDay = order ? dateOnly(order.order_date) : '';

    const canCancelNow = !!orderDay && orderDay >= hoy;
    const shouldRefund = !!orderDay && orderDay > hoy;

    const cancelInfo = useMemo(() => {
        if (!order) return { canCancelNow: false, refund: false, reason: '' };

        if (!canCancelNow) {
            return {
                canCancelNow: false,
                refund: false,
                reason: 'Solo puedes cancelar pedidos de hoy o días futuros.',
            };
        }

        if (shouldRefund) {
            return {
                canCancelNow: true,
                refund: true,
                reason: 'Al cancelar con al menos 1 día de anticipación, se devuelve el envío.',
            };
        }

        return {
            canCancelNow: true,
            refund: false,
            reason: 'Puedes cancelar hoy, pero no se devuelve el envío.',
        };
    }, [order, canCancelNow, shouldRefund]);

    if (!open) return null;

    return (
        <div className="tw-orderweek-modal__overlay" onMouseDown={onClose}>
            <div
                className="tw-orderweek-modal"
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={title || 'Detalle del pedido'}
            >
                <div className="tw-orderweek-modal__head">
                    <div className="tw-orderweek-modal__title">{title}</div>
                    <button className="tw-orderweek-iconbtn" onClick={onClose} type="button" aria-label="Cerrar">
                        <XIcon />
                    </button>
                </div>

                <div className="tw-orderweek-modal__body">
                    {!order ? (
                        <div className="tw-orderweek-alert tw-orderweek-alert--error">
                            No hay información del pedido.
                        </div>
                    ) : (
                        <>
                            <div className="tw-orderweek-modal__meta">
                                <div><b>Fecha:</b> {orderDay || '—'}</div>
                                <div><b>Tipo:</b> {String(order.type || '').toUpperCase()}</div>
                                <div><b>Estatus:</b> {order.status}</div>
                            </div>

                            <DishRow title="Plato fuerte" dish={main} />
                            {entry ? <DishRow title="Entrada / Ensalada" dish={entry} /> : null}

                            {!isDinner && snack ? <DishRow title="Snack" dish={snack} /> : null}
                        </>
                    )}

                    {mode === 'cancel' && order ? (
                        <div className="tw-orderweek-cancelbox">
                            <div className="tw-orderweek-cancelbox__title">
                                <WarningIcon /> Confirmar cancelación
                            </div>

                            <div className="tw-orderweek-cancelbox__text">
                                {cancelInfo.reason}
                            </div>

                            <div className="tw-orderweek-cancelbox__text tw-orderweek-muted">
                                Expiración del plan: <b>{expDay || '—'}</b>
                            </div>

                            {!cancelInfo.canCancelNow ? (
                                <div className="tw-orderweek-alert tw-orderweek-alert--error">
                                    Este pedido no se puede cancelar.
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <div className="tw-orderweek-modal__foot">
                    <button className="tw-orderweek-btn tw-orderweek-btn--ghost" onClick={onClose} type="button">
                        Cerrar
                    </button>

                    {mode === 'cancel' ? (
                        <button
                            className="tw-orderweek-btn tw-orderweek-btn--danger"
                            onClick={onConfirmCancel}
                            type="button"
                            disabled={isLoading || !order || !cancelInfo.canCancelNow}
                            title={!cancelInfo.canCancelNow ? 'No se puede cancelar este pedido' : ''}
                        >
                            {isLoading ? 'Cancelando...' : 'Cancelar pedido'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
